import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bell } from 'lucide-react'
import { useNotifications, useMarkNotificationRead, useMarkAllNotificationsRead } from '@/lib/notifications'
import type { NotificationDTO } from '@shared/schemas/notifications'

/** Where clicking a notification should take you — 'time_entry' and
 * 'session' notifications don't currently exist (see NOTIFIABLE_VERBS
 * in the API route), so there's nothing to link those to yet. */
function notificationHref(n: NotificationDTO): string | null {
  if (n.entityType === 'task') return `/tasks/${n.entityId}`
  if (n.entityType === 'request') return `/requests?open=${n.entityId}`
  return null
}

function relativeTime(ms: number): string {
  const seconds = Math.max(0, Math.floor((Date.now() - ms) / 1000))
  if (seconds < 60) return 'now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

export function NotificationsBell() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: notifications } = useNotifications()
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()
  const [open, setOpen] = useState(false)

  const unreadCount = notifications?.filter((n) => !n.isRead).length ?? 0

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative rounded-md border border-line p-1.5 text-ink-soft hover:text-ink"
        aria-label={t('notifications.title')}
      >
        <Bell size={15} />
        {unreadCount > 0 && (
          <span className="absolute -end-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[9px] font-bold text-brand-ink">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-30 cursor-default"
            onClick={() => setOpen(false)}
            aria-label="Close"
            tabIndex={-1}
          />
          <div className="absolute end-0 top-full z-40 mt-2 w-80 rounded-card border border-line bg-surface shadow-lg">
            <div className="flex items-center justify-between border-b border-line px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-soft">{t('notifications.title')}</p>
              {unreadCount > 0 && (
                <button type="button" onClick={() => markAllRead.mutate()} className="text-xs font-semibold text-brand">
                  {t('notifications.markAllRead')}
                </button>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto">
              {(notifications?.length ?? 0) === 0 && (
                <p className="px-3 py-6 text-center text-sm text-ink-soft">{t('notifications.empty')}</p>
              )}
              {notifications?.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => {
                    if (!n.isRead) markRead.mutate(n.id)
                    const href = notificationHref(n)
                    if (href) {
                      setOpen(false)
                      navigate(href)
                    }
                  }}
                  className={`flex w-full items-start justify-between gap-2 border-b border-line px-3 py-2.5 text-start text-sm last:border-b-0 ${
                    n.isRead ? '' : 'bg-brand/5'
                  }`}
                >
                  <span className="flex items-start gap-2">
                    {!n.isRead && <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-brand" />}
                    <span className={n.isRead ? 'text-ink-soft' : ''}>{t(`notifications.verb.${n.verb}`)}</span>
                  </span>
                  <span className="flex-none text-[11px] text-ink-soft">{relativeTime(n.createdAt)}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
